import React, { useMemo, useState, useEffect } from 'react';
import { Building2, Mail, Phone, User } from 'lucide-react';
import Modal from '../ui/Modal';
import { useSettings } from '../../context/SettingsContext';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Combobox from '../ui/Combobox';
import Button from '../ui/Button';
import toast from 'react-hot-toast';
import useApi from '../../hooks/useApi';

const FALLBACK_CATEGORIES = ['Government', 'Vendor', 'Citizen', 'Internal Organization'];

const AddressBookModal = ({ isOpen, onClose, onSuccess, contact = null, prefillContact = null }) => {
  const { settings } = useSettings();
  const { get } = useApi();
  const [loading, setLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const configuredCategories = Array.isArray(settings?.contact_categories)
    ? settings.contact_categories.filter(cat => typeof cat === 'string' && cat.trim())
    : [];
  const categoryOptions = configuredCategories.length > 0 ? configuredCategories : FALLBACK_CATEGORIES;
  const defaultCategory = categoryOptions[0] || 'Government';

  const [formData, setFormData] = useState({
    category: defaultCategory,
    organizationName: '',
    contactPerson: '',
    designation: '',
    address: '',
    email: '',
    phoneNumber: '',
    linkedUserId: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    const loadUsers = async () => {
      try {
        const response = await get('/address-book?includeUsers=1');
        const merged = Array.isArray(response?.data) ? response.data : [];
        const users = merged.filter((item) => item.sourceType === 'user');
        setUserOptions(users.map((user) => ({
          value: String(user.sourceId || user.id),
          label: `${user.displayName || user.contactPerson || user.organizationName}${user.designation ? ` (${user.designation})` : ''}`,
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
          designation: user.designation || '',
          contactPerson: user.displayName || user.contactPerson || user.organizationName || '',
          organizationName: user.organizationName || user.displayName || '',
          address: user.address || ''
        })));
      } catch (error) {
        console.error('Error loading users for address book', error);
        setUserOptions([]);
      }
    };

    loadUsers();
  }, [get, isOpen]);

  const categorySelectOptions = useMemo(
    () => categoryOptions.map(c => ({ value: c, label: c })),
    [categoryOptions]
  );

  useEffect(() => {
    const source = contact || prefillContact;
    if (source) {
      setFormData({
        category: source.category || defaultCategory,
        organizationName: source.organizationName || '',
        contactPerson: source.contactPerson || '',
        designation: source.designation || '',
        address: source.address || '',
        email: source.email || '',
        phoneNumber: source.phoneNumber || '',
        linkedUserId: source.linkedUserId ? String(source.linkedUserId) : ''
      });
    } else {
      setFormData({
        category: defaultCategory,
        organizationName: '',
        contactPerson: '',
        designation: '',
        address: '',
        email: '',
        phoneNumber: '',
        linkedUserId: ''
      });
    }
    setErrors({});
  }, [contact, prefillContact, isOpen, defaultCategory]);

  const validate = () => {
    const newErrors = {};
    if (!formData.address?.trim()) newErrors.address = 'Address is required';
    if (!formData.organizationName?.trim() && !formData.contactPerson?.trim()) {
      newErrors.organizationName = 'Either Organization or Contact Person is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;

    setLoading(true);
    try {
      const url = contact 
        ? `/api/address-book/${contact.id}`
        : '/api/address-book';
      
      const method = contact ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to save contact');
      }

      onSuccess(data.data);
      toast.success(contact ? 'Contact updated' : 'Contact saved');
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      setErrors(prev => ({ ...prev, submit: error.message }));
      toast.error(error?.message || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  const setField = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleLinkedUserChange = (userId) => {
    setField('linkedUserId', userId);
    if (!userId) return;
    const selectedUser = userOptions.find((option) => String(option.value) === String(userId));
    if (!selectedUser) return;

    setFormData(prev => ({
      ...prev,
      linkedUserId: userId,
      organizationName: prev.organizationName || selectedUser.organizationName,
      contactPerson: prev.contactPerson || selectedUser.contactPerson,
      designation: prev.designation || selectedUser.designation,
      email: prev.email || selectedUser.email,
      phoneNumber: prev.phoneNumber || selectedUser.phoneNumber,
      address: prev.address || selectedUser.address
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={contact ? "Edit Contact" : "Add New Contact"} size="xl">
      <div className="space-y-5">
        {errors.submit && (
          <div className="p-3 text-sm text-red-500 bg-red-50 rounded-md border border-red-200">
            {errors.submit}
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="lg:col-span-7 space-y-5">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-sm font-semibold text-gray-900">Basic Information</div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <Combobox
                  label="Category"
                  options={categorySelectOptions}
                  value={formData.category}
                  onChange={(v) => setField('category', v)}
                  placeholder="Select category"
                />
                <Combobox
                  label="Linked User"
                  options={[{ value: '', label: 'No linked user' }, ...userOptions]}
                  value={formData.linkedUserId}
                  onChange={handleLinkedUserChange}
                  placeholder="Attach to user"
                />
                <Input
                  id="organizationName"
                  label="Organization Name"
                  value={formData.organizationName}
                  onChange={(e) => setField('organizationName', e.target.value)}
                  error={errors.organizationName}
                  icon={Building2}
                  placeholder="e.g. Ministry of Power"
                />
                <Input
                  id="contactPerson"
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={(e) => setField('contactPerson', e.target.value)}
                  icon={User}
                  placeholder="e.g. John Doe"
                />
                <Input
                  id="designation"
                  label="Designation"
                  value={formData.designation}
                  onChange={(e) => setField('designation', e.target.value)}
                  placeholder="e.g. Director"
                />
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-sm font-semibold text-gray-900">Contact Details</div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <Input
                  id="email"
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={(e) => setField('email', e.target.value)}
                  icon={Mail}
                  placeholder="name@example.com"
                />
                <Input
                  id="phoneNumber"
                  label="Phone Number"
                  value={formData.phoneNumber}
                  onChange={(e) => setField('phoneNumber', e.target.value)}
                  icon={Phone}
                  placeholder="e.g. 0300 1234567"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-xl border border-gray-200 bg-white p-4 h-full">
              <div className="text-sm font-semibold text-gray-900">Address</div>
              <div className="mt-4">
                <Textarea
                  id="address"
                  label="Full Address"
                  value={formData.address}
                  onChange={(e) => setField('address', e.target.value)}
                  error={errors.address}
                  rows={10}
                  placeholder="Full postal address"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="secondary" onClick={onClose} className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm">
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} isLoading={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm">
            {contact ? 'Update Contact' : 'Save Contact'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddressBookModal;
